import { Ban, CalendarClock, ExternalLink } from "lucide-react";
import * as React from "react";
import { Link } from "react-router-dom";
import { callbackWhen, knownZone } from "@/lib/callbacks";
import { leadName } from "@/lib/campaign";
import { attemptsText, leadDetails } from "@/lib/dialer";
import { stageText } from "@/lib/contacts";
import { callText, languageName, listFor, localTime } from "@/lib/lead-card";
import { useCallDispositions, useLeadCard } from "@/lib/queries";
import { useNow } from "@/lib/use-now";
import { cn, formatPhoneNumber, relativeTime } from "@/lib/utils";
import { useAcdCall } from "@/softphone/acd-call";

/**
 * The lead the ACD rang this phone for (ADR-024): who they are, the time where they are, where they
 * stand on the list, and how their last call went. It is on the ringing screen, so the rep says hello
 * to a person and not to a number.
 *
 * Read from `GET /leads/{id}` once the rep's record names a lead (`useAcdCall`), and once for each
 * stay in a line. The card lists only calls that are over, so the card read for a call never has
 * that call on it; a lead who calls back a minute later is a new stay, read again, and their card
 * shows the call that just ended. Nothing personal rides the live hint that says a call is ringing
 * (ADR-018). An anonymous caller, or a lead this rep may not read, shows no card. On the call
 * screen, `full` adds the last few calls and the list's own columns.
 *
 * The firm and the stage are the CRM's (ADR-044, -045, -046): the firm the lead belongs to, and the
 * stage in the tone the contacts list gives it. Open record goes to the contact — the timeline, the
 * notes, Set outcome — without leaving the call. A callback promised and not yet followed by an
 * outcome (ADR-080) is said too: the rep calling back knows it is the call they promised.
 */
export function LeadCardPanel({ callId, full = false }: { callId: string; full?: boolean }) {
  const acd = useAcdCall();
  const leadId = acd !== null && acd.callId === callId ? acd.leadId : null;
  const { data: card } = useLeadCard(leadId, acd?.sessionId ?? acd?.callerChannelId ?? null);
  const campaignId = acd?.line?.campaign_id ?? null;
  const outcomes = useCallDispositions(campaignId, Boolean(card) && campaignId !== null);
  const now = useNow(card?.timezone ? 30_000 : null);
  if (leadId === null || !card || card.id !== leadId) return null;

  const label = (slug: string) => outcomes.data?.find((d) => d.slug === slug)?.label;
  const name = leadName(card);
  const stage = card.stage ? stageText(card.stage) : null;
  const clock = localTime(card.timezone, now);
  const where = [clock && `${clock} there`, card.state, languageName(card.language)].filter(Boolean);
  const list = listFor(card, campaignId);
  const calls = card.calls.slice(0, full ? 3 : 1);
  const details = full ? leadDetails(card.attributes, 4) : [];

  return (
    <section
      aria-label="The lead"
      className="w-full rounded-lg border border-border px-3 py-2 text-left text-xs"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-text">
            {name ?? formatPhoneNumber(card.phone)}
          </p>
          {where.length > 0 && <p className="truncate text-text-muted">{where.join(" · ")}</p>}
          {card.firm && <p className="truncate text-text-muted">{card.firm.label}</p>}
        </div>
        {stage && (
          <span
            className={cn(
              "shrink-0 rounded-md px-1.5 py-0.5 text-2xs font-medium",
              stage.tone === "warning"
                ? "bg-warning-2 text-warning-11"
                : stage.tone === "danger"
                  ? "bg-danger-2 text-danger-11"
                  : stage.tone === "neutral"
                    ? "bg-gray-3 text-gray-11"
                    : "bg-accent-3 text-accent-11",
            )}
            title={stage.description}
          >
            {stage.label}
          </span>
        )}
      </div>

      {card.dnc && (
        <p className="mt-1.5 flex items-center gap-1 font-medium text-danger-11">
          <Ban className="size-3.5 shrink-0" aria-hidden />
          Marked do not call
        </p>
      )}

      {card.callback && (
        <p
          className={cn(
            "mt-1 flex items-start gap-1",
            card.callback.status === "due" ? "text-warning-11" : "text-accent-11",
          )}
        >
          <CalendarClock className="mt-px size-3.5 shrink-0" aria-hidden />
          <span className="min-w-0">
            Callback {card.callback.status === "due" ? "due" : "set for"}{" "}
            {callbackWhen(card.callback.due_at, knownZone(card.clock_timezone ?? card.timezone)).there}
            {card.callback.rep && ` · kept for ${card.callback.rep}`}
          </span>
        </p>
      )}

      {list !== null && list.attempts > 0 && (
        <p className="mt-1 text-text-muted">{attemptsText(list, label)}</p>
      )}

      {calls.length > 0 && (
        <ul className="mt-1 flex flex-col gap-0.5" aria-label="Last calls">
          {calls.map((call) => {
            const text = callText(call, label);
            return (
              <li key={`${call.via}-${call.at}`} className="flex min-w-0 gap-2">
                <span className="shrink-0 text-text-subtle">{relativeTime(call.at)}</span>
                {/* One line while the phone rings, where Answer matters most; in full on the call. */}
                <span
                  className={cn("min-w-0 text-text", !full && "truncate")}
                  title={full ? undefined : text}
                >
                  {text}
                </span>
              </li>
            );
          })}
        </ul>
      )}

      {details.length > 0 && (
        <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 border-t border-border pt-1.5">
          {details.map(([key, value]) => (
            <React.Fragment key={key}>
              <dt className="capitalize text-text-muted">{key}</dt>
              <dd className="min-w-0 truncate text-text">{value}</dd>
            </React.Fragment>
          ))}
        </dl>
      )}

      <Link
        to={`/contacts/${card.id}`}
        className="mt-1.5 inline-flex items-center gap-1 text-2xs font-medium text-accent-11 hover:underline"
      >
        <ExternalLink className="size-3" aria-hidden />
        Open record
      </Link>
    </section>
  );
}
